import { Fragment, useState } from "react";

import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { Dialog } from "@/components/organisms/Dialog";
import { DocumentAttachedFilePreview } from "@/components/organisms/DocumentPreview";
import { Mutations, Queries } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useContextHotkeys } from "@/hooks/useContextTree";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography, typographyVariants } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

import type { TriageDetail, TriageSuggestionStub } from "./TriageView.context";

/** Which of the two is the copy: the payment in triage, or the other one. */
type TriageDuplicateSide = "payment" | "other";

type TriageDuplicatePayment = {
  id: string;
  identifier: string;
  description?: string | null;
  amount?: string | number | null;
  effectiveDate?: string | null;
  financialAccount?: { id?: string; name?: string | null } | null;
  entity?: { identifier: string; name?: string | null } | null;
  /** The statement it was read from: one of its documents. */
  lineItemOn?: { id: string } | null;
  documents: TriageDuplicateDocument[];
};

type TriageDuplicateDocument = {
  id: string;
  name?: string | null;
  createdAt: string;
  fromEmailAddress?: string | null;
  contentHash?: string | null;
  file?: { name?: string; url?: string } | null;
};

/** A payment drawn while they load: a typical one, each piece wrapped. */
const PLACEHOLDER_PAYMENT: TriageDuplicatePayment = {
  id: "placeholder",
  identifier: "placeholder",
  description: "SPOTIFY P1234",
  amount: "-12.99",
  effectiveDate: "2026-09-01T00:00:00Z",
  financialAccount: { name: "Chequing ****1234" },
  lineItemOn: { id: "placeholder" },
  documents: [
    {
      id: "placeholder",
      name: "September statement",
      createdAt: "2026-10-01T00:00:00Z",
    },
  ],
};

/**
 * A duplicate's two payments, opened from its card - by its View, or V - side
 * by side, each with the statement it came in on, the payment marked where its
 * file has it, and what the two say alike marked - so a line read twice, or
 * a file brought in twice, shows for what it is. The one picked is the copy,
 * booked against itself, and the other stays as the original. This payment is
 * the copy until the other is picked - by a click on it, or ← and →. Enter
 * books it; Close, or Escape, leaves it.
 */
export function TriageDuplicateDialog({
  triage,
  suggestion,
  onClose,
  onBook,
}: {
  triage: TriageDetail;
  /** The duplicate suggestion to open on; none, and it is shut. */
  suggestion: TriageSuggestionStub | null;
  onClose: () => void;
  /** Book the suggestion as Book books it: this payment the copy. */
  onBook: () => void;
}) {
  const other = suggestion?.ruleSuggestionTransactionOptions?.find(
    (option) => option.transaction,
  )?.transaction;
  return (
    <Dialog
      isOpen={suggestion != null && other != null}
      onClose={onClose}
      isConstrained
      renderKey="triage-duplicate"
    >
      {suggestion && other ? (
        <TriageDuplicateDialogContent
          triage={triage}
          suggestionIdentifier={suggestion.identifier}
          otherId={other.id}
          otherIdentifier={other.identifier}
          onClose={onClose}
          onBook={onBook}
        />
      ) : (
        <></>
      )}
    </Dialog>
  );
}

function TriageDuplicateDialogContent({
  triage,
  suggestionIdentifier,
  otherId,
  otherIdentifier,
  onClose,
  onBook,
}: {
  triage: TriageDetail;
  suggestionIdentifier: string;
  otherId: string;
  otherIdentifier: string;
  onClose: () => void;
  onBook: () => void;
}) {
  const [copy, setCopy] = useState<TriageDuplicateSide>("payment");
  const { data, loading } = useBackendQuery(
    Queries.TRIAGE_DUPLICATE_STATEMENTS_QUERY,
    {
      variables: {
        id: btoa(`TransactionObject:${triage.identifier}`),
        otherId,
      },
      fetchPolicy: "cache-and-network",
    },
  );
  // The other one the copy: it is booked in this one's place, and this one
  // stays in the queue as the original, its suggestions read again.
  const { mutate: bookViaSuggestion, loading: isBooking } = useBackendMutation({
    mutation: Mutations.BOOK_VIA_SUGGESTION,
    refetchQueries: [
      Queries.TRIAGE_CONNECTION_QUERY,
      Queries.TRIAGE_DETAIL_QUERY,
    ],
  });
  const payments = {
    payment:
      data?.payment && "identifier" in data.payment ? data.payment : null,
    other: data?.other && "identifier" in data.other ? data.other : null,
  };
  const isLoading = loading && !payments.payment && !payments.other;
  // Each shows the statement it was read from, or else the first document it
  // is on.
  const shown = {
    payment:
      payments.payment?.documents.find(
        (document) => document.id === payments.payment?.lineItemOn?.id,
      ) ?? payments.payment?.documents[0],
    other:
      payments.other?.documents.find(
        (document) => document.id === payments.other?.lineItemOn?.id,
      ) ?? payments.other?.documents[0],
  };
  const book = () => {
    if (isBooking) return;
    if (copy === "payment") {
      onClose();
      onBook();
      return;
    }
    void bookViaSuggestion({
      variables: {
        input: { suggestionIdentifier, duplicateIdentifier: otherIdentifier },
      },
      callbacks: {
        onSuccess: () => {
          onClose();
          State.getState().setSnackbarMessage(
            "Booked the other payment as the copy. This one stays as the original.",
          );
        },
      },
    });
  };
  useContextHotkeys({
    id: "triage-duplicate-payment",
    combos: ["arrowleft", "h"],
    callback: () => setCopy("payment"),
  });
  useContextHotkeys({
    id: "triage-duplicate-other",
    combos: ["arrowright", "l"],
    callback: () => setCopy("other"),
  });
  useContextHotkeys({
    id: "triage-duplicate-book",
    combos: "enter",
    callback: book,
  });

  return (
    <div
      data-testid="triage-duplicate-dialog"
      // The first thing in it to take the keyboard, so Enter is its own and
      // not a button's that the dialog would otherwise put it on.
      tabIndex={0}
      className="[outline:none] flex flex-col h-[min(85vh,820px)]"
    >
      <div className="pl-5 pr-5 pt-5 pb-3">
        <Typography level="title-lg">Which one is the copy?</Typography>
        <Typography level="body-sm" className="text-muted-foreground/80 mt-1">
          Each as its statement has it, marked where it is on it. The copy is
          booked against itself; the other stays as the original.
        </Typography>
      </div>
      <div className="flex-1 min-h-0 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3 pl-5 pr-5 pb-4">
        {(
          [
            { side: "payment", title: "This payment" },
            { side: "other", title: "The other payment" },
          ] as const
        ).map(({ side, title }) => (
          <TriageDuplicatePaymentColumn
            key={side}
            title={title}
            payment={payments[side] ?? PLACEHOLDER_PAYMENT}
            document={
              payments[side] ? shown[side] : PLACEHOLDER_PAYMENT.documents[0]
            }
            isLoading={isLoading}
            isCopy={copy === side}
            otherBusiness={
              payments[side]?.entity &&
              payments[side]?.entity?.identifier !== triage.entity?.identifier
                ? payments[side]?.entity?.name
                : null
            }
            onPick={() => setCopy(side)}
          />
        ))}
      </div>
      <div className="flex justify-end gap-2 p-3 [border-top-width:1px] [border-top-style:solid] border-border">
        <Button variant="outline" onClick={onClose}>
          Close
          <HotkeyDecorator hotkey="escape" color="inherit" />
        </Button>
        <Button
          data-testid="triage-duplicate-book"
          disabled={isBooking || isLoading}
          onClick={book}
        >
          {isBooking && <Spinner />}
          {copy === "payment"
            ? "Book this payment as the copy"
            : "Book the other payment as the copy"}
          <HotkeyDecorator hotkey="enter" color="inherit" />
        </Button>
      </div>
    </div>
  );
}

/**
 * One of the two: what it says, and the document it is on with the payment
 * marked on the file.
 */
function TriageDuplicatePaymentColumn({
  title,
  payment,
  document,
  isLoading,
  isCopy,
  otherBusiness,
  onPick,
}: {
  title: string;
  payment: TriageDuplicatePayment;
  /** The statement it was read from, or else the first document it is on. */
  document?: TriageDuplicateDocument;
  isLoading: boolean;
  isCopy: boolean;
  /** Its business, where it is not this payment's. */
  otherBusiness?: string | null;
  onPick: () => void;
}) {
  const facts = [
    payment.effectiveDate ? Utils.fDate(new Date(payment.effectiveDate)) : null,
    payment.amount != null
      ? Utils.fCurrency(Number(payment.amount), false, true)
      : null,
    payment.financialAccount?.name ?? null,
  ].filter((fact) => fact != null);
  // Where it is on the file: its amount however the statement writes it -
  // a sign or brackets, a currency sign, thousands set apart or not - and its
  // words, however they are spaced.
  const [whole, cents] = Math.abs(Number(payment.amount ?? 0))
    .toFixed(2)
    .split(".");
  const highlight = [
    new RegExp(
      `[-−(]?\\$?\\s?${whole.replace(/\B(?=(\d{3})+$)/g, ",?")}\\.${cents}\\)?(?:\\s?(?:-|CR\\b|DR\\b))?`,
      "i",
    ),
    ...(payment.description?.trim()
      ? [
          new RegExp(
            payment.description
              .trim()
              .split(/\s+/)
              .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
              .join("\\s*"),
            "i",
          ),
        ]
      : []),
  ];
  return (
    <div
      data-testid="triage-duplicate-payment"
      data-copy={isCopy ? "" : undefined}
      onClick={onPick}
      className={cn(
        "flex flex-col min-h-0 cursor-pointer rounded-md [border-width:2px] [border-style:solid] border-[initial]",
        isCopy ? "border-primary" : "border-border",
        "overflow-hidden",
      )}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 gap-y-0.5 p-3 [border-bottom-width:1px] [border-bottom-style:solid] border-border">
        <Typography level="body-xs" className="text-muted-foreground/80">
          {title}
          {otherBusiness ? ` · ${otherBusiness}` : ""}
        </Typography>
        <div className="[grid-row:1_/_span_2] col-2 [align-self:start]">
          <SimpleEnum
            label={isCopy ? "Copy" : "Original"}
            enumColor={isCopy ? EnumColors.BLUE : EnumColors.GREY_1}
            size="sm"
          />
        </div>
        <Typography level="title-sm" className="truncate">
          <Skeleton loading={isLoading} variant="inline">
            {payment.description}
          </Skeleton>
        </Typography>
        <Typography
          level="body-sm"
          className="col-span-full text-muted-foreground"
        >
          <Skeleton loading={isLoading} variant="inline">
            {facts.map((fact, index) => (
              <Fragment key={index}>
                {index > 0 ? " · " : null}
                {fact}
              </Fragment>
            ))}
          </Skeleton>
        </Typography>
        <div
          data-testid="triage-duplicate-document"
          className="col-span-full flex items-center gap-2 min-w-0"
        >
          <Typography
            level="body-xs"
            className="truncate flex-1 min-w-0 text-muted-foreground/80"
          >
            <Skeleton loading={isLoading} variant="inline">
              {document ? (
                <>
                  {document.name || document.file?.name
                    ? `On ${document.name || document.file?.name}`
                    : "On a document with no name"}
                  {` · ${
                    document.fromEmailAddress
                      ? `emailed by ${document.fromEmailAddress}`
                      : "uploaded"
                  } ${Utils.fDate(new Date(document.createdAt))}`}
                </>
              ) : (
                "From the bank feed - no statement"
              )}
            </Skeleton>
          </Typography>
          {document?.file?.url && !isLoading ? (
            <a
              href={document.file.url}
              target="_blank"
              rel="noopener noreferrer"
              // Its own: opening the file is not picking the payment.
              onClick={(event) => event.stopPropagation()}
              className={cn(
                typographyVariants({
                  level: "body-xs",
                }),
                "text-primary underline-offset-4 hover:underline",
              )}
            >
              Open
            </a>
          ) : null}
        </div>
      </div>
      <Skeleton
        loading={isLoading}
        variant="rectangular"
        className="flex-1 min-h-0"
      >
        <div className="flex-1 min-h-0 overflow-y-auto">
          {document?.file?.url ? (
            <DocumentAttachedFilePreview
              key={document.id}
              file={document.file}
              highlight={highlight}
            />
          ) : (
            <Typography
              level="body-sm"
              className="p-8 text-center text-muted-foreground/80"
            >
              {document
                ? "This document has no file to show."
                : "It came in from the bank feed, not a document."}
            </Typography>
          )}
        </div>
      </Skeleton>
    </div>
  );
}
